/**
 * Agency Portal v2 §4.7 — faithful but LIGHT preview of the hosted
 * report page, rendered inside the dashboard. The real marketing
 * `ReportPage` cannot be imported here (apps/marketing can't be a
 * dependency of apps/dashboard), so this component reconstructs the
 * same visual language against a canned sample: header, 2 px accent
 * rule, four signal cards (two pass / two miss), CTA, "Powered by
 * Sill" footer.
 *
 * Everything here is sample data — no live scan, no API call, no DB
 * write. The preview updates as the dashboard editor types.
 */

import { Check, X } from 'lucide-react';

import {
  pickTextOnAccent,
  wcagAssessAgainstTheme,
} from './wl-contrast.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

/** Sill ink + cream, pinned here so the preview reflects the LIGHT
 *  marketing report pages (which render in light mode only for agency
 *  reports, per agency-portal-v2.md §7.1). */
const LIGHT = {
  bg: '#faf6f1',
  ink: '#1a1410',
  muted: '#6b645a',
  border: '#e5ddd0',
  surface: '#ffffff',
  lime: '#d9ff3f',
  limeFg: '#1a1410',
  coral: '#ff6b47',
};

export interface WhiteLabelReportPreviewProps {
  readonly displayName: string;
  readonly logoUrl: string | null;
  readonly accentHex: string | null;
  readonly contactUrl: string | null;
  /** Agency Portal v3 §5.4 — chosen CTA mode. `null` falls back to
   *  the smart default (slug → claim_sill, contactUrl → custom,
   *  else → none). */
  readonly ctaMode?: 'claim_sill' | 'custom' | 'none' | null;
  /** Agency Portal v3.2 — surfaces whether the agency has a referral
   *  slug; the smart default picks claim_sill when true. */
  readonly hasReferralSlug?: boolean;
  readonly whiteLabel: boolean;
}

interface SignalRow {
  readonly title: string;
  readonly body: string;
  readonly status: 'FOUND' | 'ALLOWED' | 'MISSING';
}

const SAMPLE_SIGNALS: readonly SignalRow[] = [
  {
    title: 'Agent card',
    body: 'Your site publishes an agent card: who you are and what AI assistants can do on your site.',
    status: 'FOUND',
  },
  {
    title: 'Sitemap',
    body: "Your sitemap lists your pages so assistants don't have to guess.",
    status: 'FOUND',
  },
  {
    title: 'AI access (robots.txt)',
    body: 'Your robots.txt lets the major AI assistants read your site.',
    status: 'ALLOWED',
  },
  {
    title: 'llms.txt',
    body: 'A short plain-text summary of your business for AI assistants.',
    status: 'MISSING',
  },
  {
    title: 'MCP server',
    body: "Your site has an MCP server, so AI assistants can use your site's tools directly.",
    status: 'FOUND',
  },
  {
    title: 'Content signals',
    body: "Your robots.txt doesn't say how AI may use your content (search, answers, or training).",
    status: 'MISSING',
  },
];

export function WhiteLabelReportPreview({
  displayName,
  logoUrl,
  accentHex,
  contactUrl,
  ctaMode = null,
  hasReferralSlug = false,
  whiteLabel,
}: WhiteLabelReportPreviewProps): JSX.Element {
  // v3.2 (2026-10-10) §5.4 — resolve the effective CTA mode. When
  // `ctaMode` is null (unsaved default), pick the "smart default" from
  // the inputs: slug → claim_sill, contactUrl → custom, else → none.
  // This mirrors the radio picker's effective state so the preview
  // and the picker always agree.
  const effectiveCta: 'claim_sill' | 'custom' | 'none' =
    ctaMode === 'claim_sill' && hasReferralSlug
      ? 'claim_sill'
      : ctaMode === 'claim_sill' && contactUrl !== null && contactUrl.length > 0
        ? 'custom'
        : ctaMode === 'custom' && contactUrl !== null && contactUrl.length > 0
          ? 'custom'
          : ctaMode === 'none'
            ? 'none'
            : ctaMode === null && hasReferralSlug
              ? 'claim_sill'
              : contactUrl !== null && contactUrl.length > 0
                ? 'custom'
                : 'none';
  // Accent choice — fall back to Sill lime if the agency's hex isn't a
  // valid #rrggbb OR if contrast is below 3:1 against both backgrounds
  // (same discipline as the real `report-render` renderer).
  const assessment =
    accentHex !== null && accentHex.length > 0 ? wcagAssessAgainstTheme(accentHex) : null;
  const safeAccent =
    assessment !== null && assessment.tier !== 'low' ? accentHex! : LIGHT.lime;
  const textOnAccent = pickTextOnAccent(safeAccent) === 'white' ? '#ffffff' : LIGHT.ink;
  const nameRendered = displayName.trim().length > 0 ? displayName.trim() : 'Your agency';

  return (
    <div
      data-testid="white-label-report-preview"
      style={{
        background: LIGHT.bg,
        color: LIGHT.ink,
        padding: 20,
        border: `1px solid ${LIGHT.border}`,
        borderRadius: 2,
        fontFamily: sansFont,
        fontSize: 12,
        lineHeight: 1.5,
      }}
    >
      {/* Header: logo + name on the left; kicker on the right. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          {logoUrl !== null ? (
            // eslint-disable-next-line jsx-a11y/alt-text -- alt empty when name is next to it (§7.2 row 1).
            <img
              src={logoUrl}
              alt=""
              referrerPolicy="no-referrer"
              loading="lazy"
              decoding="async"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
              style={{ height: 20, width: 'auto', maxWidth: 160, maxHeight: 20 }}
            />
          ) : null}
          <div style={{ fontFamily: sansFont, fontSize: 14, fontWeight: 600, color: LIGHT.ink }}>
            {nameRendered}
          </div>
        </div>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            letterSpacing: 2,
            color: LIGHT.muted,
            textTransform: 'uppercase',
          }}
        >
          AGENT-READINESS REPORT
        </div>
      </div>
      {/* 2 px accent rule — the only visual the accent paints when
          white_label is on. */}
      <div
        aria-hidden
        style={{
          height: 2,
          background: whiteLabel ? safeAccent : LIGHT.lime,
          marginTop: 12,
          marginBottom: 16,
        }}
      />
      {!whiteLabel ? (
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            letterSpacing: 1.5,
            color: LIGHT.muted,
            textTransform: 'uppercase',
            marginBottom: 12,
          }}
        >
          Prepared by {nameRendered} · Powered by Sill
        </div>
      ) : null}
      <h1
        style={{
          fontFamily: serifFont,
          fontSize: 24,
          lineHeight: 1.1,
          color: LIGHT.ink,
          marginBottom: 10,
          letterSpacing: -0.5,
        }}
      >
        example-store.example is mostly ready for AI agents. 6 of 8 checks pass.
      </h1>
      <div style={{ fontFamily: sansFont, fontSize: 12, color: LIGHT.muted, marginBottom: 16 }}>
        WordPress site · Checked October 10, 2026
      </div>

      {SAMPLE_SIGNALS.slice(0, 4).map((row) => (
        <SignalCard key={row.title} row={row} accent={safeAccent} />
      ))}

      {/* CTA — v3 (2026-10-10) picks the button per report_cta_mode.
          `claim_sill` → "Claim …" button (lime, Sill-standard look).
          `custom`     → "Talk to {Agency}" button (agency accent).
          `none`       → the muted "reply to the email" line.          */}
      {effectiveCta === 'claim_sill' ? (
        <div style={{ marginTop: 14 }} data-testid="white-label-report-preview-claim">
          <a
            href="#preview-only"
            onClick={(e) => e.preventDefault()}
            style={{
              display: 'inline-block',
              padding: '10px 16px',
              background: LIGHT.lime,
              color: LIGHT.limeFg,
              fontFamily: sansFont,
              fontSize: 13,
              fontWeight: 600,
              textDecoration: 'none',
              borderRadius: 2,
            }}
          >
            Claim example-store.example →
          </a>
          <div style={{ marginTop: 6, fontFamily: sansFont, fontSize: 11, color: LIGHT.muted }}>
            Attributes signups to {nameRendered} via your referral link.
          </div>
        </div>
      ) : effectiveCta === 'custom' && contactUrl !== null ? (
        <div style={{ marginTop: 14 }}>
          <a
            href={contactUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              padding: '10px 16px',
              background: safeAccent,
              color: textOnAccent,
              fontFamily: sansFont,
              fontSize: 13,
              fontWeight: 600,
              textDecoration: 'none',
              borderRadius: 2,
            }}
            data-testid="white-label-report-preview-contact"
          >
            Talk to {nameRendered} →
          </a>
        </div>
      ) : (
        <div
          style={{
            marginTop: 14,
            padding: '10px 12px',
            border: `1px dashed ${LIGHT.border}`,
            borderRadius: 2,
            fontFamily: sansFont,
            fontSize: 11,
            color: LIGHT.muted,
            lineHeight: 1.5,
          }}
          data-testid="white-label-report-preview-contact-placeholder"
        >
          No button. The report shows a quiet line asking the reader to
          reply to the email you sent.
        </div>
      )}

      <div
        style={{
          fontFamily: sansFont,
          fontSize: 11,
          color: LIGHT.muted,
          marginTop: 20,
          paddingTop: 10,
          borderTop: `1px solid ${LIGHT.border}`,
        }}
      >
        Powered by Sill
      </div>
    </div>
  );
}

function SignalCard({ row, accent: _accent }: { row: SignalRow; accent: string }): JSX.Element {
  const isMiss = row.status === 'MISSING';
  // Pass marks always render in Sill lime, regardless of the agency's
  // accent — matches the LIVE marketing `ReportPage` renderer (which
  // uses `C.lime` for the Check icon) and the help text under the
  // Accent colour input ("Sill lime stays the primary colour for pass
  // marks"). Previously these dots were painted in the agency accent,
  // which drifted the preview away from what the agency's clients
  // actually see.
  const statusBg = isMiss ? LIGHT.surface : LIGHT.lime;
  const statusText = isMiss ? LIGHT.coral : LIGHT.limeFg;
  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        padding: '10px 0',
        borderTop: `1px solid ${LIGHT.border}`,
        alignItems: 'flex-start',
      }}
    >
      <span
        aria-hidden
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 20,
          height: 20,
          borderRadius: 999,
          background: statusBg,
          color: statusText,
          flex: '0 0 auto',
          marginTop: 2,
          border: isMiss ? `1px solid ${LIGHT.coral}` : 'none',
        }}
      >
        {isMiss ? <X size={11} /> : <Check size={11} />}
      </span>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            fontWeight: 500,
            color: LIGHT.ink,
            display: 'flex',
            alignItems: 'baseline',
            gap: 10,
          }}
        >
          <span>{row.title}</span>
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              letterSpacing: 1.5,
              color: isMiss ? LIGHT.coral : LIGHT.muted,
              textTransform: 'uppercase',
            }}
          >
            {row.status}
          </span>
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 12, color: LIGHT.muted, marginTop: 2 }}>
          {row.body}
        </div>
      </div>
    </div>
  );
}
